import { createEntityAdapter, createSlice, type EntityState, type PayloadAction } from '@reduxjs/toolkit'

import { sharedHydrated } from '@/store/shared-actions'

import type { ProjectEvent, ProjectRecord, ProjectUser } from './types'

export const projectsAdapter = createEntityAdapter<ProjectRecord>()
export const projectUsersAdapter = createEntityAdapter<ProjectUser>()
export const eventsAdapter = createEntityAdapter<ProjectEvent>()

export interface ProjectsState {
  projects: EntityState<ProjectRecord, string>
  projectUsers: EntityState<ProjectUser, string>
  events: EntityState<ProjectEvent, string>
}

export const emptyProjects = (): ProjectsState => ({
  projects: projectsAdapter.getInitialState(),
  projectUsers: projectUsersAdapter.getInitialState(),
  events: eventsAdapter.getInitialState(),
})

export const projectsSlice = createSlice({
  name: 'projects',
  initialState: emptyProjects(),
  reducers: {
    /** Creates or replaces a project record. */
    projectSaved(state, action: PayloadAction<ProjectRecord>) {
      projectsAdapter.upsertOne(state.projects, action.payload)
    },
    /** Replaces the project list with what the backend holds (the records are already merged with what is kept here). */
    projectsLoaded(state, action: PayloadAction<ProjectRecord[]>) {
      projectsAdapter.setAll(state.projects, action.payload)
    },
    projectPatched(state, action: PayloadAction<{ id: string; changes: Partial<Omit<ProjectRecord, 'id'>> }>) {
      projectsAdapter.updateOne(state.projects, action.payload)
    },
    /**
     * The people the backend says are on a project. Their rows carry an id starting `map:`, so loading again replaces exactly the
     * rows that came from the backend and never touches assignments made here. `projectId` limits it to one project's rows (every
     * project when null); `userId` limits it to one person's rows, for the projects a person is on.
     */
    projectTeamLoaded(state, action: PayloadAction<{ projectId: string | null; userId?: string; assignments: ProjectUser[] }>) {
      const { projectId, userId, assignments } = action.payload
      const stale = state.projectUsers.ids.filter((id) => {
        const item = state.projectUsers.entities[id]
        return String(id).startsWith('map:') && item && (projectId === null || item.projectId === projectId) && (userId === undefined || item.userId === userId)
      })
      projectUsersAdapter.removeMany(state.projectUsers, stale)
      projectUsersAdapter.upsertMany(state.projectUsers, assignments)
    },
    projectUsersAdded(state, action: PayloadAction<ProjectUser[]>) {
      projectUsersAdapter.addMany(state.projectUsers, action.payload)
    },
    /** Events are append-only. `addMany` ignores an id that already exists, so a replayed event never duplicates. */
    eventsAdded(state, action: PayloadAction<ProjectEvent[]>) {
      eventsAdapter.addMany(state.events, action.payload)
    },
  },
  extraReducers: (builder) => {
    builder.addCase(sharedHydrated, (_state, action) => action.payload.data.projects)
  },
})

export const { projectSaved, projectsLoaded, projectPatched, projectTeamLoaded, projectUsersAdded, eventsAdded } = projectsSlice.actions
